/**
 * GeoJSON layer factory for polygons, lines, and point data.
 * Renders borders, chokepoints, flood zones, and cyclone tracks from GeoJSON files.
 * Cables and pipelines use PathLayer with static data imports (no fetch).
 */

import { GeoJsonLayer, PathLayer } from '@deck.gl/layers';
import type { Feature, FeatureCollection } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { UNDERSEA_CABLES, PIPELINES, PIPELINE_COLORS } from '@/config/data';
import type { UnderseaCable, Pipeline } from '@/config/data';
import { OVERLAY_COLORS } from '@/map/layer-colors';

/** Layer IDs that render as GeoJsonLayer (polygons, lines, polyline tracks). */
export const GEOJSON_LAYER_IDS = new Set([
  'borders', 'undersea-cables', 'pipelines-oil', 'pipelines-gas',
  'chokepoints', 'floods', 'cyclones',
]);

/** Mapping from layer ID to GeoJSON file path (only for fetched data). */
const GEOJSON_FILE_MAP: Record<string, string> = {
  'borders': '/data/borders-disputed.geojson',
  'chokepoints': '/data/chokepoints.geojson',
  'floods': '/data/flood-zones.geojson',
  'cyclones': '/data/cyclone-tracks.geojson',
};

/** In-memory cache for loaded GeoJSON data. */
const geoJsonCache = new Map<string, FeatureCollection>();

/** Fetch and cache a GeoJSON file. Returns cached version if available. */
export async function loadGeoJsonData(layerId: string): Promise<FeatureCollection> {
  const cached = geoJsonCache.get(layerId);
  if (cached) return cached;

  const filePath = GEOJSON_FILE_MAP[layerId];
  if (!filePath) {
    return { type: 'FeatureCollection', features: [] };
  }

  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      console.warn(`[Sentinel] Failed to load ${filePath}: ${response.status}`);
      return { type: 'FeatureCollection', features: [] };
    }
    const data = await response.json() as FeatureCollection;
    geoJsonCache.set(layerId, data);
    return data;
  } catch (err) {
    console.warn(`[Sentinel] Error loading ${filePath}:`, err);
    return { type: 'FeatureCollection', features: [] };
  }
}

/**
 * Synchronous read of the GeoJSON cache.
 * Used by dedicated renderers (static-point-layer) that need the loaded
 * FeatureCollection without re-fetching.
 */
export function getGeoJsonData(layerId: string): FeatureCollection | undefined {
  return geoJsonCache.get(layerId);
}

/** Check if a layer ID requires an async GeoJSON fetch (vs. static import). */
export function isGeoJsonFetchLayer(id: string): boolean {
  return id in GEOJSON_FILE_MAP;
}

/** Check if a layer's GeoJSON data is already cached. */
export function isGeoJsonCached(id: string): boolean {
  return geoJsonCache.has(id);
}

/** Styling config per GeoJSON layer type. */
interface GeoJsonStyle {
  lineColor: [number, number, number, number];
  fillColor: [number, number, number, number];
  lineWidth: number;
  dashed: boolean;
}

const GEOJSON_STYLES: Record<string, GeoJsonStyle> = {
  'borders': {
    lineColor: [100, 116, 139, 200],
    fillColor: [100, 116, 139, 20],
    lineWidth: 1.5,
    dashed: true,
  },
  'chokepoints': {
    lineColor: [239, 68, 68, 200],
    fillColor: [239, 68, 68, 30],
    lineWidth: 2,
    dashed: false,
  },
  'floods': {
    lineColor: [6, 182, 212, 120],
    fillColor: [6, 182, 212, 40],
    lineWidth: 1,
    dashed: false,
  },
  'cyclones': {
    lineColor: [168, 85, 247, 200],
    fillColor: [0, 0, 0, 0],
    lineWidth: 2.5,
    dashed: false,
  },
};

const DEFAULT_STYLE: GeoJsonStyle = {
  lineColor: [100, 116, 139, 150],
  fillColor: [100, 116, 139, 20],
  lineWidth: 1,
  dashed: false,
};

// ---------------------------------------------------------------------------
// Hex color string to RGBA array conversion (for PIPELINE_COLORS)
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, alpha: number): [number, number, number, number] {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return [r, g, b, alpha];
}

/** Pre-computed RGBA pipeline colors for deck.gl (from hex PIPELINE_COLORS). */
const PIPELINE_RGBA: Record<string, [number, number, number, number]> = {};
for (const [type, hex] of Object.entries(PIPELINE_COLORS)) {
  PIPELINE_RGBA[type] = hexToRgba(hex, 150);
}

// ---------------------------------------------------------------------------
// PathLayer factories for cables and pipelines (static data, no fetch)
// ---------------------------------------------------------------------------

function createCablesPathLayer(): Layer {
  return new PathLayer<UnderseaCable>({
    id: 'cables-layer',
    data: UNDERSEA_CABLES,
    getPath: (d) => d.points,
    getColor: OVERLAY_COLORS.cable as [number, number, number, number],
    getWidth: 1,
    widthMinPixels: 1,
    pickable: true,
  });
}

function createPipelinesPathLayer(pipelineType: 'oil' | 'gas'): Layer {
  const filtered = PIPELINES.filter((p) => p.type === pipelineType);
  return new PathLayer<Pipeline>({
    id: `pipelines-${pipelineType}-layer`,
    data: filtered,
    getPath: (d) => d.points,
    getColor: (d) => PIPELINE_RGBA[d.type] ?? [150, 150, 150, 150],
    getWidth: 1.5,
    widthMinPixels: 1,
    pickable: true,
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create a layer for the given layer ID.
 * Cables and pipelines use PathLayer from static imports.
 * Everything else uses GeoJsonLayer from fetched GeoJSON.
 */
export function createGeoJsonLayer(
  layerId: string,
  zoom: number,
): Layer {
  // Static data layers — PathLayer, no fetch needed
  if (layerId === 'undersea-cables') {
    return createCablesPathLayer();
  }
  if (layerId === 'pipelines-oil') {
    return createPipelinesPathLayer('oil');
  }
  if (layerId === 'pipelines-gas') {
    return createPipelinesPathLayer('gas');
  }

  // Fetched GeoJSON layers
  const data = geoJsonCache.get(layerId);
  const style = GEOJSON_STYLES[layerId] ?? DEFAULT_STYLE;
  const opacity = Math.min(0.9, 0.3 + zoom * 0.08);

  return new GeoJsonLayer<Feature>({
    id: `geojson-${layerId}`,
    data: data ?? { type: 'FeatureCollection' as const, features: [] },
    pickable: true,
    filled: true,
    stroked: true,
    pointType: '',
    getFillColor: style.fillColor,
    getLineColor: style.lineColor,
    getLineWidth: style.lineWidth,
    lineWidthUnits: 'pixels' as const,
    opacity,
    ...(style.dashed ? { getDashArray: [6, 4] } : {}),
  });
}

/**
 * Pre-load GeoJSON data for all active layers that have a file mapping.
 * Only fetches layers that require GeoJSON files (not static data layers).
 */
export async function preloadGeoJsonLayers(activeLayerIds: string[]): Promise<void> {
  const toLoad = activeLayerIds.filter(
    (id) => id in GEOJSON_FILE_MAP && !geoJsonCache.has(id),
  );
  await Promise.allSettled(toLoad.map((id) => loadGeoJsonData(id)));
}
